import { queryOptions, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const YAML_PREVIEW_QUERY_KEY = 'yaml-preview';

const getYamlPreviewQueryKey = ({
  yamlLink,
  token,
}: {
  yamlLink?: string;
  token?: string;
}) => [YAML_PREVIEW_QUERY_KEY, yamlLink, token] as const;

const getYamlPreviewQueryOptions = ({
  yamlLink,
  token,
  enabled = true,
}: {
  yamlLink?: string;
  token?: string;
  enabled?: boolean;
} = {}) =>
  queryOptions<string, AxiosError>({
    queryKey: getYamlPreviewQueryKey({ yamlLink, token }),
    queryFn: async () => {
      const { data } = await apiClient.yamlPreview.getYaml(yamlLink!, token!);
      return data;
    },
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    enabled: makeIsEnabled(!!token, !!yamlLink, enabled),
  });

type UseYamlPreviewQueryArgs = {
  token?: string;
  yamlLink?: string;
  enabled?: boolean;
};

export const useYamlPreviewQuery = ({
  token,
  yamlLink,
  enabled = true,
}: UseYamlPreviewQueryArgs) => {
  const { isLoading, data, error } = useQuery(
    getYamlPreviewQueryOptions({ yamlLink, token, enabled })
  );

  return {
    isLoading,
    yaml: data,
    error,
  };
};
